import React from 'react';
import { Link } from './common/NextAdapters';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { Carousel, CarouselContent, CarouselItem, useCarousel } from './ui/carousel';

export function MediaRow({ heading, note, href, children, className }) {
  const slides = buildSlides(children);

  return (
    <section className={cn('group/row py-2.5 sm:py-3', className)}>
      <div className="mx-auto flex max-w-[1600px] items-end gap-4 gutter">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground display">{heading}</h2>
        {note && (
          <p className="hidden pb-0.5 text-xs sm:text-sm text-muted-foreground sm:block">{note}</p>
        )}
        {href && (
          <Link
            href={href}
            className="group/all ml-auto flex shrink-0 items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-xs sm:text-sm text-muted-foreground transition-colors hover:border-white/25 hover:text-foreground focus-visible:outline-none"
          >
            See all
            <Icon
              icon="ph:caret-right-bold"
              className="size-3 transition-transform duration-200 group-hover/all:translate-x-0.5"
            />
          </Link>
        )}
      </div>

      <div className="mx-auto mt-2 max-w-[1600px] gutter relative">
        <Carousel
          opts={{ align: 'start', loop: true, slidesToScroll: 1 }}
          aria-label={heading}
        >
          <CarouselContent className="-ml-3 sm:-ml-4">{slides}</CarouselContent>
          <RowArrow side="left" heading={heading} />
          <RowArrow side="right" heading={heading} />
        </Carousel>
      </div>
    </section>
  );
}

const ENOUGH_TO_LOOP = 10; // poster rows show ~7 cards on wide screens, so the loop needs more slides than that

function buildSlides(children) {
  const items = React.Children.toArray(children);
  if (items.length < 2 || items.length >= ENOUGH_TO_LOOP) return items;

  const passes = Math.ceil(ENOUGH_TO_LOOP / items.length);
  const slides = [];
  for (let pass = 0; pass < passes; pass += 1) {
    items.forEach((item, index) => {
      slides.push(React.cloneElement(item, { key: `${pass}:${index}` }));
    });
  }
  return slides;
}

function RowArrow({ side, heading }) {
  const { scrollPrev, scrollNext } = useCarousel();
  const left = side === 'left';

  return (
    <button
      type="button"
      onClick={left ? scrollPrev : scrollNext}
      aria-label={`Scroll ${heading} ${left ? 'backwards' : 'forwards'}`}
      tabIndex={-1}
      aria-hidden
      className={cn(
        'absolute top-0 bottom-14 z-30 hidden w-12 items-center justify-center lg:flex cursor-pointer',
        'opacity-0 transition-opacity duration-200 group-hover/row:opacity-100',
        left
          ? 'left-0 rounded-l-lg bg-gradient-to-r from-[#07050e]/95 via-[#07050e]/70 to-transparent'
          : 'right-0 rounded-r-lg bg-gradient-to-l from-[#07050e]/95 via-[#07050e]/70 to-transparent'
      )}
    >
      <Icon
        icon={left ? 'ph:caret-left-bold' : 'ph:caret-right-bold'}
        className="size-6 text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]"
      />
    </button>
  );
}

// Slide widths per card shape: landscape = 16:9 cards, poster = tall 2:3 cards (more per screen),
// ranked = poster plus the oversized Top-10 number that sits to its left.
const ROW_ITEM_BASIS = {
  landscape: 'basis-[72%] sm:basis-[46%] lg:basis-[31%] xl:basis-[23.5%]',
  poster: 'basis-[40%] sm:basis-[28%] md:basis-[21%] lg:basis-[16.5%] xl:basis-[13.5%]',
  ranked: 'basis-[54%] sm:basis-[38%] md:basis-[29%] lg:basis-[22%] xl:basis-[18%]',
};

export function RowItem({ children, className, variant = 'landscape' }) {
  return (
    <CarouselItem
      className={cn(ROW_ITEM_BASIS[variant] || ROW_ITEM_BASIS.landscape, 'pl-3 sm:pl-4', className)}
    >
      {children}
    </CarouselItem>
  );
}

export default MediaRow;
